const JSON_HEADERS = { 'Content-Type': 'application/json' };

export async function fetchGenerations() {
  const response = await fetch('/api/generations');
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

export async function createGenerationRecord(payload) {
  const response = await fetch('/api/record', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function updateGenerationRecord(id, payload) {
  const response = await fetch(`/api/generations/${id}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    // Avant : le fetch se resolvait meme sur 400/500 (seul un echec reseau
    // rejetait la promesse), donc un appelant qui fait juste `await` sans
    // verifier response.ok croyait la mise a jour reussie malgre une erreur
    // de validation serveur (cas reel : regeneration du mot cible d'une
    // punchline non-latine dans le dashboard, cf. dashboardCard.js).
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export function deleteGenerationRecord(id) {
  return fetch(`/api/generations/${id}`, { method: 'DELETE' });
}

export function reorderGenerations(ids) {
  return fetch('/api/generations/reorder', {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ ids }),
  });
}

// POST /api/knockout ne fait plus qu'un aller-retour rapide (validation +
// identifiant de job) -- le calcul reel (15-90s) tourne côté serveur dans
// un process isolé, plutôt que de garder cette seule requête HTTP ouverte
// tout ce temps (le proxy devant le site coupait la connexion à 90s). Le
// contrat de cette fonction ne change pas pour ses appelants
// (proposerFeature.js, dashboardCard.js) : toujours une promesse qui se
// résout en {svg, lineData} ou rejette avec une erreur.
// onQueueUpdate (optionnel) est rappelé avec la position dans la file
// d'attente serveur (cf. knockoutService.js -- MAX_CONCURRENT forks a la
// fois) : 1 = le prochain a demarrer, 0 = le calcul tourne reellement.
// Permet a l'appelant (proposerFeature.js) d'afficher "En file d'attente"
// plutot que "Génération en cours" tant que ce n'est pas encore lance.
export async function generateKnockout(payload, onQueueUpdate) {
  const response = await fetch('/api/knockout', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  const { jobId, queuePosition } = await response.json();
  onQueueUpdate?.(queuePosition || 0);
  return waitForKnockoutResult(jobId, onQueueUpdate);
}

// Double mécanisme pour récupérer le résultat une fois prêt, jobId à
// l'appui : un événement SSE ("knockout-result", diffusé à tous les
// onglets ouverts -- rapide quand il arrive) ET un sondage régulier de
// GET /api/knockout/:jobId (fiable dans tous les cas -- constaté à
// l'usage que la livraison SSE d'un événement retardé de dizaines de
// secondes n'est pas fiable partout, contrairement à un aller-retour HTTP
// classique). Le premier des deux qui répond gagne, l'autre est arrêté.
function waitForKnockoutResult(jobId, onQueueUpdate) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let lastQueuePosition = null;
    let timer = null;

    function cleanup() {
      settled = true;
      clearTimeout(timer);
      clearInterval(pollTimer);
      es.removeEventListener('knockout-result', onResult);
      es.close();
    }

    function settle(data) {
      if (settled) return;
      cleanup();
      if (data.ok) resolve({ svg: data.svg, lineData: data.lineData });
      else reject(new Error(data.error || 'Erreur de génération knockout'));
    }

    // Reamorce a chaque progres constate (position en file qui change, ou
    // passage a "en cours") plutot qu'un delai fixe depuis la requete
    // initiale -- avec la file d'attente serveur (knockoutService.js,
    // MAX_CONCURRENT), quelqu'un en position 4-5 peut legitimement
    // patienter plus de 150s avant que son tour arrive ; un timer fixe
    // depuis le depart aurait donne un faux "Délai dépassé" pendant que
    // la generation progresse normalement (constate en relisant le code
    // avant une demo avec beaucoup de monde en meme temps, 24 septembre
    // 2026). Ce delai protege seulement contre une VRAIE absence de
    // progres (ex. job bloque), pas contre une file simplement longue.
    function armTimer() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (settled) return;
        cleanup();
        reject(new Error('Délai de génération dépassé'));
      }, 150_000);
    }
    armTimer();

    const es = createEventsStream();
    function onResult(event) {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (data.jobId !== jobId) return;
      settle(data);
    }
    es.addEventListener('knockout-result', onResult);

    const pollTimer = setInterval(async () => {
      if (settled) return;
      try {
        const res = await fetch(`/api/knockout/${jobId}`);
        if (!res.ok) return;
        const job = await res.json();
        if (job.status === 'pending') {
          const position = job.queuePosition || 0;
          if (position !== lastQueuePosition) {
            lastQueuePosition = position;
            onQueueUpdate?.(position);
            armTimer();
          }
          return;
        }
        settle({ ok: job.status === 'ok', svg: job.result?.svg, lineData: job.result?.lineData, error: job.error });
      } catch {
        // Échec réseau ponctuel -- on retentera au prochain intervalle.
      }
    }, 4000);
  });
}

export function createEventsStream() {
  return new EventSource('/api/events');
}

// includeHidden : avec les photos masquées (dashboard, admin).
export async function fetchArPhotos({ includeHidden = false } = {}) {
  const response = await fetch(includeHidden ? '/api/ar-photos?hidden=include' : '/api/ar-photos');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

export function updateArPhoto(id, payload) {
  return fetch(`/api/ar-photos/${id}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
}

export function deleteArPhoto(id) {
  return fetch(`/api/ar-photos/${id}`, { method: 'DELETE' });
}

// includeHidden : avec les photos masquées (dashboard, admin).
export async function fetchMapPhotos({ includeHidden = false } = {}) {
  const response = await fetch(includeHidden ? '/api/map-photos?hidden=include' : '/api/map-photos');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

export function updateMapPhoto(id, payload) {
  return fetch(`/api/map-photos/${id}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
}

export function deleteMapPhoto(id) {
  return fetch(`/api/map-photos/${id}`, { method: 'DELETE' });
}

// includeHidden : avec les photos masquées (dashboard, admin).
export async function fetchSpherePhotos({ includeHidden = false } = {}) {
  const response = await fetch(includeHidden ? '/api/sphere-photos?hidden=include' : '/api/sphere-photos');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

export function updateSpherePhoto(id, payload) {
  return fetch(`/api/sphere-photos/${id}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
}

export function deleteSpherePhoto(id) {
  return fetch(`/api/sphere-photos/${id}`, { method: 'DELETE' });
}

export async function uploadSpherePhoto(formData) {
  const response = await fetch('/api/sphere-photos', { method: 'POST', body: formData });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function uploadMapPhoto(formData) {
  const response = await fetch('/api/map-photos', { method: 'POST', body: formData });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchContributors() {
  const response = await fetch('/api/contributors');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

// Contenu utilisable par le générateur de posters : punchlines + photos non
// masquées avec leurs dimensions (cf. posterPoolService.js).
export async function fetchPosterPool() {
  const response = await fetch('/api/posters/pool');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

// Validation d'un poster : le serveur fabrique le fichier HD (quelques
// secondes) et renvoie { id, downloads: { png, jpg } }.
export async function createPoster(payload) {
  const response = await fetch('/api/posters', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || `HTTP ${response.status}`);
  return body;
}

// includeHidden : avec les posters masqués (dashboard, admin).
export async function fetchPosters({ includeHidden = false } = {}) {
  const response = await fetch(includeHidden ? '/api/posters?hidden=include' : '/api/posters');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

export function updatePoster(id, payload) {
  return fetch(`/api/posters/${id}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
}

export function deletePoster(id) {
  return fetch(`/api/posters/${id}`, { method: 'DELETE' });
}

export async function fetchShopCatalog({ limit = 24, offset = 0, query = '' } = {}) {
  const searchParams = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (query) searchParams.set('query', query);

  const response = await fetch(`/api/shop/catalog?${searchParams.toString()}`);
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchShopVariants(productId) {
  const response = await fetch(`/api/shop/catalog/${productId}/variants`);
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchShopPreviewData({ productId, variantId, placement }) {
  const searchParams = new URLSearchParams();
  if (placement) searchParams.set('placement', placement);

  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const response = await fetch(`/api/shop/catalog/${productId}/variants/${variantId}/preview${suffix}`);
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function uploadShopArtwork(formData) {
  const response = await fetch('/api/shop/artworks', {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function createShopOrder(payload) {
  const response = await fetch('/api/shop/orders', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function createShopCheckout(payload) {
  const response = await fetch('/api/shop/checkout', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function estimateShopShipping(payload) {
  const response = await fetch('/api/shop/shipping-rates', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchAdminSession() {
  const response = await fetch('/api/admin/session');
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function loginAdmin(password) {
  const response = await fetch('/api/admin/login', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ password }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function logoutAdmin() {
  const response = await fetch('/api/admin/logout', { method: 'POST' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

